import { useState } from "react";
import { isEmpty, isEqual } from "lodash";
import { AICC_SANDBOX_DIRECTIVE, AiccSandboxBaseScope, AiccSandboxMode } from "constants/aiccPromptSandbox";
import { AiccSandboxCursor, AiccSandboxInputs, AiccSandboxTurnResult } from "types/model/aiccPromptSandbox";
import { AiccSandboxLessonRequest, AiccSandboxTurnRequest } from "types/dto/request/aiccPromptSandbox";
import * as sandboxAPI from "services/aiccPromptSandbox";
import { sandboxRequestBase } from "../sandboxRequest";
import { cursorRequest } from "../Lesson/lessonRequest";
import { EMPTY_CONVERSATION, SandboxConversation, continuedConversation, openedConversation } from "./conversation";
import { hasLesson, hasOpenerVariant, hasWrapUp } from "../sandboxScope";

interface Options {
  languageId?: number;
  mode: AiccSandboxMode;
  variant?: string;
  inputs: AiccSandboxInputs;
  baseScope: AiccSandboxBaseScope;
  maxRounds: number;
  isFirstConversation: boolean;
  lesson?: AiccSandboxLessonRequest;
}

export function useSandboxConversation({
  languageId,
  mode,
  variant,
  inputs,
  baseScope,
  maxRounds,
  isFirstConversation,
  lesson,
}: Options) {
  const [conversation, setConversation] = useState<SandboxConversation>(EMPTY_CONVERSATION);
  const [lastRun, setLastRun] = useState<AiccSandboxTurnResult>();
  const [runInputs, setRunInputs] = useState<AiccSandboxInputs>();
  const [runLesson, setRunLesson] = useState<AiccSandboxLessonRequest>();
  const [runIsFirst, setRunIsFirst] = useState<boolean>();
  const [runVariant, setRunVariant] = useState<string>();
  const [isRunning, setIsRunning] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string>();
  const [isWrapUpSent, setIsWrapUpSent] = useState(false);
  // 🔴 Not derivable from the transcript: a retry appends a bubble but keeps `line_idx`.
  const [cursor, setCursor] = useState<AiccSandboxCursor>();

  const isStarted = !isEmpty(conversation.transcript);

  const requestBase = () => ({
    ...sandboxRequestBase(inputs, baseScope),
    ...(hasLesson(mode) && lesson ? { lesson } : {}),
  });

  const run = (payload: Omit<AiccSandboxTurnRequest, "mode">, onResult: (result: AiccSandboxTurnResult) => void) => {
    if (!languageId) {
      return;
    }

    setIsRunning(true);
    sandboxAPI
      .runAiccSandboxTurn(languageId, {
        ...payload,
        mode,
        variant,
        // 🔴 Every turn: the backend is stateless, and an omitted `max_rounds` is production's 18.
        max_rounds: hasWrapUp(mode) ? maxRounds : undefined,
      })
      .then((result) => {
        onResult(result);
        setLastRun(result);
        setCursor(result.cursor);

        if (result.appendedDirective === AICC_SANDBOX_DIRECTIVE.wrapup) {
          setIsWrapUpSent(true);
        }

        setRunInputs(inputs);
        setRunVariant(variant);
        setRunLesson(lesson);
      })
      .catch(() => undefined)
      .finally(() => {
        setIsRunning(false);
        setPendingMessage(undefined);
      });
  };

  const start = () =>
    run(
      {
        ...requestBase(),
        transcript: [],
        // 🔴 Lesson review opens by sending no cursor; it has no opener call.
        ...(hasLesson(mode) ? {} : { seed_opener: true }),
        ...(hasOpenerVariant(mode) ? { is_first_conversation: isFirstConversation } : {}),
      },
      (result) => {
        setConversation(openedConversation(result, mode));
        setRunIsFirst(hasOpenerVariant(mode) ? isFirstConversation : undefined);
      }
    );

  const send = (typed: string, isWrapUp = false) => {
    setPendingMessage(typed);

    return run(
      {
        ...requestBase(),
        transcript: conversation.transcript,
        message: typed,
        inject: isWrapUp ? "wrapup" : undefined,
        cursor: cursorRequest(cursor),
      },
      (result) => setConversation((current) => continuedConversation(current, typed, result))
    );
  };

  const rerun = () => {
    setConversation(EMPTY_CONVERSATION);
    setCursor(undefined);
    setIsWrapUpSent(false);
    setLastRun(undefined);
    setRunIsFirst(undefined);
    setRunVariant(undefined);
    start();
  };

  return {
    conversation,
    pendingMessage,
    lastRun,
    isRunning,
    isStarted,
    isWrapUpArmed: !!lastRun?.isWrapupArmed,
    isWrapUpSent,
    cursor,
    runInputs,
    runLesson,
    areDetailsChanged: !!runInputs && !isEqual(runInputs, inputs),
    isOpenerChanged: runIsFirst !== undefined && runIsFirst !== isFirstConversation,
    isVariantChanged: !!runVariant && runVariant !== variant,
    isLessonChanged: !!runLesson && !!lesson && !isEqual(runLesson, lesson),
    start,
    send,
    rerun,
  };
}
